import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, AlertTriangle, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { importProspects, qualificationImport } from '../../lib/campaignsApi';

const REQUIRED = ['company_name', 'country'];
const TEMPLATE_HEADERS = [
  'company_name', 'website', 'country', 'city', 'company_type',
  'contact_page_url', 'public_email', 'public_phone', 'whatsapp',
  'linkedin_url', 'brands_sold', 'notes',
  'buyer_potential_tier', 'estimated_annual_units', 'recommended_action',
  'market',
  'assigned_owner',
];
const TEMPLATE_CSV =
  TEMPLATE_HEADERS.join(',') + '\n' +
  'CSBYTE,https://csbyte.cl,Chile,Santiago,Refurbished Computer Reseller (B2B),https://csbyte.cl/pages/contacto,contacto@csbyte.cl,+56 9 7483 5654,+56 9 7483 5654,https://www.linkedin.com/company/csbyte/,HP Lenovo Dell,Premium B2B refurb — 6000+ enterprise customers,A,500,Email + Call,South America,Ernesto\n';

// Feb-2026 — Qualification update-only mode.
const QUAL_HEADERS = [
  'id', 'email', 'campaign_audience', 'campaign_eligibility',
  'qualification_confidence', 'qualification_reason',
  // Feb-2026 follow-up — language accepted here too so a researched
  // full-export round-trip lands on the same prospect. Blank leaves
  // the existing language untouched; invalid values silently
  // normalise to 'Unknown' (matches Mode A semantics).
  'language',
];
const QUAL_TEMPLATE_CSV =
  QUAL_HEADERS.join(',') + '\n' +
  ',contacto@csbyte.cl,Channel Buyer,Ready for New Campaign,High,Verified LATAM channel buyer,Spanish\n' +
  ',ventas@example.com,Corporate / Institutional,Manual Review,Medium,Needs revenue confirmation,English\n';

const HEADER_ALIASES = {
  company_name: 'company_name', company: 'company_name',
  country: 'country',
  city: 'city',
  website: 'website', url: 'website',
  company_type: 'company_type', type: 'company_type', segment: 'company_type',
  contact_page_url: 'contact_page_url', contact_page: 'contact_page_url',
  public_email: 'public_email', email: 'public_email',
  sales_email: 'public_email', purchasing_email: 'public_email',
  public_phone: 'public_phone', phone: 'public_phone', telephone: 'public_phone',
  whatsapp: 'whatsapp', wa: 'whatsapp',
  linkedin_url: 'linkedin_url', linkedin: 'linkedin_url',
  linkedin_company_url: 'linkedin_url',
  brands_sold: 'brands_sold', brands: 'brands_sold',
  notes: 'notes', note: 'notes', comments: 'notes',
  buyer_potential_tier: 'buyer_potential_tier', tier: 'buyer_potential_tier', buyer_tier: 'buyer_potential_tier',
  estimated_annual_units: 'estimated_annual_units', annual_units: 'estimated_annual_units', units: 'estimated_annual_units',
  recommended_action: 'recommended_action', action: 'recommended_action',
  territory: 'territory', region: 'territory', market: 'territory',
  assigned_owner: 'assigned_owner', owner: 'assigned_owner', sales_owner: 'assigned_owner',
};

// Qualification-mode aliases (kept small — every alias falls back to the raw column name).
const QUAL_HEADER_ALIASES = {
  id: 'id', prospect_id: 'id',
  email: 'email', public_email: 'email', contact_email: 'email',
  campaign_audience: 'campaign_audience', audience: 'campaign_audience',
  campaign_eligibility: 'campaign_eligibility', eligibility: 'campaign_eligibility',
  qualification_confidence: 'qualification_confidence', confidence: 'qualification_confidence',
  qualification_reason: 'qualification_reason', reason: 'qualification_reason',
  language: 'language', lang: 'language',
};

const normHeader = (h) => (h || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const parseCSV = (text) => {
  const rows = []; let cur = ['']; let i = 0; let inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i++; continue; }
      cur[cur.length - 1] += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ',') { cur.push(''); i++; continue; }
    if (ch === '\r') { i++; continue; }
    if (ch === '\n') { rows.push(cur); cur = ['']; i++; continue; }
    cur[cur.length - 1] += ch; i++;
  }
  if (cur.length > 1 || cur[0] !== '') rows.push(cur);
  return rows;
};

const downloadTemplate = (mode) => {
  const csv = mode === 'qualification' ? QUAL_TEMPLATE_CSV : TEMPLATE_CSV;
  const name = mode === 'qualification'
    ? 'luxortek-prospects-qualification-template.csv'
    : 'luxortek-prospects-import-template.csv';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

export const ProspectImportModal = ({ onClose, onImported }) => {
  // Feb-2026 — mode toggle. 'create' = existing "create new" flow.
  //           'qualification' = update-only qualification fields.
  const [mode, setMode] = useState('create');
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewingQual, setPreviewingQual] = useState(false);
  const [qualPreview, setQualPreview] = useState(null);   // dry-run response
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const resetAll = () => { setFile(null); setParsed(null); setResult(null); setQualPreview(null); };

  const handleModeChange = (next) => {
    if (next === mode) return;
    setMode(next);
    resetAll();
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDone = () => {
    if (result && onImported) onImported(result);
    onClose();
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) { toast.error('Please choose a .csv file'); return; }
    const text = await f.text();
    const arr = parseCSV(text).filter((r) => r.some((c) => c && c.trim() !== ''));
    if (arr.length < 2) { toast.error('CSV must have a header row and at least one data row'); return; }

    const rawHeaders = arr[0].map(normHeader);

    if (mode === 'qualification') {
      const headerMap = rawHeaders.map((h) => QUAL_HEADER_ALIASES[h] || h);
      const idxOf = (canonical) => headerMap.indexOf(canonical);
      const dataRows = arr.slice(1).map((row) => ({
        id: (row[idxOf('id')] || '').trim(),
        email: (row[idxOf('email')] || '').trim(),
        campaign_audience: (row[idxOf('campaign_audience')] || '').trim(),
        campaign_eligibility: (row[idxOf('campaign_eligibility')] || '').trim(),
        qualification_confidence: (row[idxOf('qualification_confidence')] || '').trim(),
        qualification_reason: (row[idxOf('qualification_reason')] || '').trim(),
        language: (row[idxOf('language')] || '').trim(),
      }));
      const hasKey = dataRows.some((r) => r.id || r.email);
      if (!hasKey) {
        toast.error('Qualification CSV needs at least one row with id or email');
        return;
      }
      setFile(f);
      setParsed({
        detectedHeaders: rawHeaders,
        totalRows: dataRows.length,
        rows: dataRows,
        preview: dataRows.slice(0, 5),
      });
      setResult(null);
      setQualPreview(null);
      return;
    }

    // Create-new mode (unchanged).
    const headerMap = rawHeaders.map((h) => HEADER_ALIASES[h] || h);
    const missing = REQUIRED.filter((req) => !headerMap.includes(req));
    const idxOf = (canonical) => headerMap.indexOf(canonical);
    const dataRows = arr.slice(1).map((row) => {
      const get = (k) => { const i = idxOf(k); return i >= 0 ? (row[i] || '').trim() : ''; };
      const unitsRaw = get('estimated_annual_units');
      const unitsNum = unitsRaw === '' ? null : (Number.isFinite(parseInt(unitsRaw, 10)) ? parseInt(unitsRaw, 10) : null);
      return {
        company_name: get('company_name'),
        country: get('country'),
        city: get('city'),
        website: get('website'),
        company_type: get('company_type'),
        contact_page_url: get('contact_page_url'),
        public_email: get('public_email'),
        public_phone: get('public_phone'),
        whatsapp: get('whatsapp'),
        linkedin_url: get('linkedin_url'),
        brands_sold: get('brands_sold'),
        notes: get('notes'),
        buyer_potential_tier: get('buyer_potential_tier'),
        estimated_annual_units: unitsNum,
        recommended_action: get('recommended_action'),
        territory: get('territory'),
        assigned_owner: get('assigned_owner'),
      };
    });
    const validRows = dataRows.filter((r) => r.company_name && r.country);
    setFile(f);
    setParsed({
      detectedHeaders: rawHeaders,
      missingHeaders: missing,
      totalRows: dataRows.length,
      validRows,
      invalidRows: dataRows.length - validRows.length,
      preview: dataRows.slice(0, 5),
    });
    setResult(null);
  };

  const runImport = async () => {
    if (mode === 'create') {
      if (!parsed || parsed.validRows.length === 0) return;
      try {
        setSubmitting(true);
        const r = await importProspects(parsed.validRows);
        setResult(r.data);
        const { imported, skipped_duplicates, invalid_rows } = r.data;
        toast.success(`Imported ${imported}, skipped ${skipped_duplicates} duplicate${skipped_duplicates === 1 ? '' : 's'}${invalid_rows ? `, ${invalid_rows} invalid` : ''}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Import failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Qualification apply (post-confirm)
    if (!qualPreview || !parsed?.rows?.length) return;
    try {
      setSubmitting(true);
      const r = await qualificationImport(parsed.rows, false);
      setResult(r.data);
      const { records_to_update = 0 } = r.data.counts || {};
      toast.success(`Applied qualification to ${records_to_update} prospect${records_to_update === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Qualification import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const previewQualification = async () => {
    if (!parsed?.rows?.length) return;
    try {
      setPreviewingQual(true);
      const r = await qualificationImport(parsed.rows, true);
      setQualPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewingQual(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="import-prospects-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-[720px] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-white font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4 text-[#00D4FF]" />
            Import Prospects from CSV
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="prospect-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2" data-testid="prospect-import-mode-toggle">
            <button
              onClick={() => handleModeChange('create')}
              className={`text-left border p-3 transition-colors ${mode === 'create' ? 'border-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 bg-[#050505] hover:border-white/25'}`}
              data-testid="mode-create-new"
              aria-pressed={mode === 'create'}
            >
              <div className={`text-xs uppercase tracking-wider ${mode === 'create' ? 'text-[#00D4FF]' : 'text-white/50'}`}>Mode A</div>
              <div className="text-white text-sm font-semibold mt-0.5">Create new prospects</div>
              <div className="text-white/55 text-[11px] mt-1">Add rows to the prospect database. Duplicates are skipped.</div>
            </button>
            <button
              onClick={() => handleModeChange('qualification')}
              className={`text-left border p-3 transition-colors ${mode === 'qualification' ? 'border-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 bg-[#050505] hover:border-white/25'}`}
              data-testid="mode-update-qualification"
              aria-pressed={mode === 'qualification'}
            >
              <div className={`text-xs uppercase tracking-wider ${mode === 'qualification' ? 'text-[#00D4FF]' : 'text-white/50'}`}>Mode B</div>
              <div className="text-white text-sm font-semibold mt-0.5">Update existing (qualification only)</div>
              <div className="text-white/55 text-[11px] mt-1">Match by prospect ID first, then email. Never creates new records.</div>
            </button>
          </div>

          <div className="border border-white/10 bg-[#050505] p-3 text-xs text-white/65" data-testid="prospect-import-help">
            {mode === 'create' ? (
              <>
                <div className="font-semibold text-white/85 mb-1">Accepted columns</div>
                <div className="font-mono text-[11px] text-[#00D4FF] break-all">{TEMPLATE_HEADERS.join(', ')}</div>
                <div className="mt-2 text-white/45">
                  Required: <span className="text-white/80">company_name</span>, <span className="text-white/80">country</span>. All others optional. Duplicates are detected by website (or company name if website empty).
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold text-white/85 mb-1">Accepted columns (qualification update)</div>
                <div className="font-mono text-[11px] text-[#00D4FF] break-all">{QUAL_HEADERS.join(', ')}</div>
                <div className="mt-2 text-white/45">
                  Each row needs <span className="text-white/80">id</span> or <span className="text-white/80">email</span>. Blank enum values leave the field unchanged. Missing matches are reported (never created). The optional <span className="text-white/80">language</span> column accepts <span className="text-white/70">English / Spanish / Portuguese / Unknown</span> — blank leaves the existing language untouched; invalid values normalise to <span className="text-white/70">Unknown</span>.
                </div>
                <div className="mt-1 text-white/45">
                  Enums — Audience: <span className="text-white/70">Channel Buyer</span>, <span className="text-white/70">Integrator / MSP</span>, <span className="text-white/70">Corporate / Institutional</span>, <span className="text-white/70">Other / Unclassified</span>. Eligibility: <span className="text-white/70">Ready for New Campaign</span>, <span className="text-white/70">Follow-Up Only</span>, <span className="text-white/70">Direct Personal Follow-Up</span>, <span className="text-white/70">Manual Review</span>, <span className="text-white/70">Exclude</span>. Confidence: <span className="text-white/70">High</span>, <span className="text-white/70">Medium</span>, <span className="text-white/70">Low</span>.
                </div>
              </>
            )}
            <button
              onClick={() => downloadTemplate(mode)}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#00D4FF] hover:underline"
              data-testid="prospect-template-btn"
            >
              <Download className="w-3 h-3" /> Download template CSV
            </button>
          </div>

          {!result && (
            <div>
              <label htmlFor="prospect-csv-input" className="block border border-dashed border-white/15 hover:border-[#00D4FF]/50 bg-[#050505] p-6 text-center cursor-pointer transition-colors" data-testid="prospect-dropzone">
                <Upload className="w-6 h-6 mx-auto text-white/40 mb-2" />
                <div className="text-white/75 text-sm">{file ? file.name : 'Click to choose a CSV file'}</div>
                <div className="text-white/40 text-[11px] mt-1">UTF-8 encoded</div>
              </label>
              <input id="prospect-csv-input" ref={inputRef} type="file" accept=".csv,text/csv" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="prospect-file-input" />
            </div>
          )}

          {/* Create-new mode preview */}
          {mode === 'create' && parsed && !result && (
            <div className="space-y-3" data-testid="prospect-import-preview">
              {parsed.missingHeaders.length > 0 ? (
                <div className="border border-red-400/30 bg-red-400/5 p-3 text-red-200/90 text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Missing required column{parsed.missingHeaders.length === 1 ? '' : 's'}: <span className="font-mono">{parsed.missingHeaders.join(', ')}</span>
                    <div className="text-red-200/60 mt-1">Detected: <span className="font-mono">{parsed.detectedHeaders.join(', ')}</span></div>
                  </div>
                </div>
              ) : (
                <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-xs flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>Parsed <span className="font-mono">{parsed.totalRows}</span> rows — <span className="font-mono">{parsed.validRows.length}</span> valid, <span className="font-mono">{parsed.invalidRows}</span> invalid.</div>
                </div>
              )}
              {parsed.preview.length > 0 && (
                <div className="border border-white/10 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#050505] text-white/55 uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Company</th>
                        <th className="text-left px-2 py-1.5">Country</th>
                        <th className="text-left px-2 py-1.5">City</th>
                        <th className="text-left px-2 py-1.5">Type</th>
                        <th className="text-left px-2 py-1.5">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.preview.map((r, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-2 py-1.5 text-white/85">{r.company_name || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.country || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.city || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.company_type || '—'}</td>
                          <td className="px-2 py-1.5 text-[#00D4FF]">{r.public_email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.totalRows > 5 && (
                    <div className="px-2 py-1.5 text-[11px] text-white/40 bg-[#050505] border-t border-white/5">…and {parsed.totalRows - 5} more rows</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Qualification mode preview + dry-run counts */}
          {mode === 'qualification' && parsed && !result && (
            <div className="space-y-3" data-testid="prospect-qual-preview">
              <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-xs flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>Parsed <span className="font-mono">{parsed.totalRows}</span> qualification row{parsed.totalRows === 1 ? '' : 's'}. Click <span className="text-white/85">Preview matches</span> to see how many prospects will be updated.</div>
              </div>

              {parsed.preview.length > 0 && (
                <div className="border border-white/10 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#050505] text-white/55 uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="text-left px-2 py-1.5">ID / Email</th>
                        <th className="text-left px-2 py-1.5">Audience</th>
                        <th className="text-left px-2 py-1.5">Eligibility</th>
                        <th className="text-left px-2 py-1.5">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.preview.map((r, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-2 py-1.5 text-white/85 font-mono text-[10px]">{r.id || r.email || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.campaign_audience || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.campaign_eligibility || '—'}</td>
                          <td className="px-2 py-1.5 text-white/65">{r.qualification_confidence || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.totalRows > 5 && (
                    <div className="px-2 py-1.5 text-[11px] text-white/40 bg-[#050505] border-t border-white/5">…and {parsed.totalRows - 5} more rows</div>
                  )}
                </div>
              )}

              {qualPreview && (
                <div className="border border-white/10 bg-[#050505] p-3 text-xs" data-testid="qual-preview-counts">
                  <div className="text-white/85 font-semibold mb-2">Preview — no changes applied yet</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                    <div className="border border-emerald-400/20 bg-emerald-400/5 p-2">
                      <div className="text-emerald-200/70 uppercase tracking-wider text-[10px]">Records to update</div>
                      <div className="text-emerald-100 text-lg font-semibold" data-testid="qual-count-to-update">{qualPreview.counts?.records_to_update ?? 0}</div>
                    </div>
                    <div className="border border-amber-400/20 bg-amber-400/5 p-2">
                      <div className="text-amber-200/70 uppercase tracking-wider text-[10px]">Not matched</div>
                      <div className="text-amber-100 text-lg font-semibold" data-testid="qual-count-not-matched">{qualPreview.counts?.records_not_matched ?? 0}</div>
                    </div>
                    <div className="border border-amber-400/20 bg-amber-400/5 p-2">
                      <div className="text-amber-200/70 uppercase tracking-wider text-[10px]">Duplicate emails</div>
                      <div className="text-amber-100 text-lg font-semibold" data-testid="qual-count-duplicates">{qualPreview.counts?.duplicate_emails ?? 0}</div>
                    </div>
                    <div className="border border-red-400/20 bg-red-400/5 p-2">
                      <div className="text-red-200/70 uppercase tracking-wider text-[10px]">Invalid rows</div>
                      <div className="text-red-100 text-lg font-semibold" data-testid="qual-count-invalid">{qualPreview.counts?.invalid_rows ?? 0}</div>
                    </div>
                    <div className="border border-white/10 bg-black/40 p-2">
                      <div className="text-white/50 uppercase tracking-wider text-[10px]">Skipped (blank)</div>
                      <div className="text-white/85 text-lg font-semibold" data-testid="qual-count-skipped">{qualPreview.counts?.skipped_rows ?? 0}</div>
                    </div>
                  </div>
                  {(qualPreview.invalid?.length > 0 || qualPreview.not_matched?.length > 0 || qualPreview.duplicates?.length > 0) && (
                    <details className="mt-2 text-white/60">
                      <summary className="cursor-pointer">View details</summary>
                      <div className="mt-2 space-y-2 max-h-40 overflow-auto font-mono text-[11px]">
                        {qualPreview.invalid?.length > 0 && (
                          <div><div className="text-red-300/80 mb-1">Invalid</div>{qualPreview.invalid.slice(0, 20).map((x, i) => <div key={i}>row {x.row}: {x.reason}</div>)}</div>
                        )}
                        {qualPreview.not_matched?.length > 0 && (
                          <div><div className="text-amber-300/80 mb-1">Not matched</div>{qualPreview.not_matched.slice(0, 20).map((x, i) => <div key={i}>row {x.row}: id={x.id || '∅'} email={x.email || '∅'}</div>)}</div>
                        )}
                        {qualPreview.duplicates?.length > 0 && (
                          <div><div className="text-amber-300/80 mb-1">Duplicates</div>{qualPreview.duplicates.slice(0, 20).map((x, i) => <div key={i}>row {x.row}: {x.email} — {x.reason}</div>)}</div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Result (both modes) */}
          {result && (
            <div className="space-y-2" data-testid="prospect-import-result">
              {mode === 'create' ? (
                <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-sm flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold mb-1">Import complete</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside text-emerald-200/80">
                      <li><span data-testid="prospect-result-imported">{result.imported}</span> new prospects added</li>
                      <li><span data-testid="prospect-result-skipped">{result.skipped_duplicates}</span> duplicate{result.skipped_duplicates === 1 ? '' : 's'} skipped</li>
                      <li><span data-testid="prospect-result-invalid">{result.invalid_rows}</span> invalid row{result.invalid_rows === 1 ? '' : 's'} rejected</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="border border-emerald-400/20 bg-emerald-400/5 p-3 text-emerald-200/90 text-sm flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold mb-1">Qualification applied</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside text-emerald-200/80">
                      <li><span data-testid="qual-result-applied">{result.applied ?? result.counts?.records_to_update ?? 0}</span> prospect{(result.applied ?? 0) === 1 ? '' : 's'} updated</li>
                      <li><span data-testid="qual-result-not-matched">{result.counts?.records_not_matched ?? 0}</span> not matched (never created)</li>
                      <li><span data-testid="qual-result-duplicates">{result.counts?.duplicate_emails ?? 0}</span> duplicate email row{(result.counts?.duplicate_emails ?? 0) === 1 ? '' : 's'} ignored</li>
                      <li><span data-testid="qual-result-invalid">{result.counts?.invalid_rows ?? 0}</span> invalid row{(result.counts?.invalid_rows ?? 0) === 1 ? '' : 's'} rejected</li>
                    </ul>
                  </div>
                </div>
              )}
              {result.errors && result.errors.length > 0 && (
                <details className="border border-amber-400/20 bg-amber-400/5 p-2 text-amber-200/85 text-xs">
                  <summary className="cursor-pointer">{result.errors.length} validation error{result.errors.length === 1 ? '' : 's'}</summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-auto font-mono text-[11px]">
                    {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-white/5 px-5 py-3 flex justify-end gap-2">
          {result ? (
            <button onClick={handleDone} className="btn-primary text-sm" data-testid="prospect-import-done">Done</button>
          ) : mode === 'create' ? (
            <>
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={runImport}
                disabled={!parsed || parsed.missingHeaders.length > 0 || parsed.validRows.length === 0 || submitting}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="prospect-import-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Import {parsed?.validRows?.length || 0} row{(parsed?.validRows?.length || 0) === 1 ? '' : 's'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              {!qualPreview ? (
                <button
                  onClick={previewQualification}
                  disabled={!parsed?.rows?.length || previewingQual}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="qual-preview-btn"
                >
                  {previewingQual ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Preview matches
                </button>
              ) : (
                <button
                  onClick={runImport}
                  disabled={submitting || (qualPreview.counts?.records_to_update || 0) === 0}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="qual-apply-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Confirm & apply to {qualPreview.counts?.records_to_update || 0} prospect{(qualPreview.counts?.records_to_update || 0) === 1 ? '' : 's'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectImportModal;
